import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { createContext, useContext, useState } from 'react';
import axios from 'axios';

export const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Auth context
const AuthCtx = createContext(null);
export function useAuth() { return useContext(AuthCtx); }

export function authFetch(url, opts = {}) {
  const token = localStorage.getItem('kc_token');
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

export const api = axios.create({ baseURL: API });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('kc_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
api.interceptors.response.use(r => r, err => {
  if (err.response?.status === 401) {
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_user');
    window.location.href = '/giris';
  }
  return Promise.reject(err);
});

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const expire = localStorage.getItem('kc_expire');
      if (expire && Date.now() > parseInt(expire)) {
        localStorage.removeItem('kc_token');
        localStorage.removeItem('kc_user');
        localStorage.removeItem('kc_expire');
        localStorage.removeItem('kc_remember');
        return null;
      }
      return JSON.parse(localStorage.getItem('kc_user'));
    } catch { return null; }
  });

  const login = (token, kullanici) => {
    localStorage.setItem('kc_token', token);
    localStorage.setItem('kc_user', JSON.stringify(kullanici));
    setUser(kullanici);
  };

  const logout = () => {
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_user');
    setUser(null);
  };

  const updateUser = (data) => {
    const updated = { ...user, ...data };
    localStorage.setItem('kc_user', JSON.stringify(updated));
    setUser(updated);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, updateUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

function PrivateRoute({ children, roller }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/giris" replace />;
  if (roller && !roller.includes(user.rol)) return <Navigate to="/dashboard" replace />;
  return children;
}

import Landing from './pages/Landing';
import Kvkk from './pages/Kvkk';
import Gizlilik from './pages/Gizlilik';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profil from './pages/Profil';
import Kurumlar from './pages/Kurumlar';
import Binalar from './pages/Binalar';
import Lokasyonlar from './pages/Lokasyonlar';
import Anketler from './pages/Anketler';
import Checklistler from './pages/Checklistler';
import QRYonetim from './pages/QRYonetim';
import AnketForm from './pages/AnketForm';
import ChecklistForm from './pages/ChecklistForm';
import QrRedirect from './pages/QrRedirect';
import AnketSonuclar from './pages/AnketSonuclar';
import ChecklistKayitlar from './pages/ChecklistKayitlar';
import Raporlar from './pages/Raporlar';
import Kullanicilar from './pages/Kullanicilar';
import SmsAyarlari from './pages/SmsAyarlari';
import SiteAyarlari from './pages/SiteAyarlari';
import Layout from './components/Layout';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          {/* ── Public pages ── */}
          <Route path="/" element={<Landing />} />
          <Route path="/kvkk" element={<Kvkk />} />
          <Route path="/gizlilik" element={<Gizlilik />} />

          {/* ── Auth ── */}
          <Route path="/giris" element={<Login />} />

          {/* ── Public QR / Anket ── */}
          <Route path="/qr/:uuid" element={<QrRedirect />} />
          <Route path="/anket/:uuid" element={<AnketForm />} />
          <Route path="/checklist/:uuid" element={
            <PrivateRoute><ChecklistForm /></PrivateRoute>
          } />

          {/* ── Admin Panel (pathless layout) ── */}
          <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profil" element={<Profil />} />

            {/* SuperAdmin only */}
            <Route path="/kurumlar" element={
              <PrivateRoute roller={['superadmin']}><Kurumlar /></PrivateRoute>
            } />
            <Route path="/site-ayarlari" element={
              <PrivateRoute roller={['superadmin']}><SiteAyarlari /></PrivateRoute>
            } />

            {/* Yönetici + SuperAdmin */}
            <Route path="/binalar" element={
              <PrivateRoute roller={['superadmin','yonetici']}><Binalar /></PrivateRoute>
            } />
            <Route path="/lokasyonlar" element={
              <PrivateRoute roller={['superadmin','yonetici']}><Lokasyonlar /></PrivateRoute>
            } />
            <Route path="/anketler" element={
              <PrivateRoute roller={['superadmin','yonetici']}><Anketler /></PrivateRoute>
            } />
            <Route path="/checklistler" element={
              <PrivateRoute roller={['superadmin','yonetici']}><Checklistler /></PrivateRoute>
            } />
            <Route path="/qr-yonetim" element={
              <PrivateRoute roller={['superadmin','yonetici']}><QRYonetim /></PrivateRoute>
            } />
            <Route path="/kullanicilar" element={
              <PrivateRoute roller={['superadmin','yonetici']}><Kullanicilar /></PrivateRoute>
            } />
            <Route path="/sms-ayarlari" element={
              <PrivateRoute roller={['superadmin','yonetici']}><SmsAyarlari /></PrivateRoute>
            } />

            {/* All roles */}
            <Route path="/anket-sonuclar" element={<AnketSonuclar />} />
            <Route path="/checklist-kayitlar" element={<ChecklistKayitlar />} />
            <Route path="/raporlar" element={<Raporlar />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
